<script setup lang="ts">
/**
 * API 金鑰（`/api-keys`，`meta.roles: ['superadmin']`；批147）。
 *
 * 金鑰沿用網頁同一組 `/api/admin/*`：**權限＝擁有者（必為超級管理者）∩ 金鑰勾選的範圍**。
 * 管理端點只認網頁登入、只有超級管理者（金鑰不能管理金鑰）。
 *
 * - 清單＝全部超級管理者的金鑰（新到舊）；狀態過濾是**下拉**（預設「有效」），
 *   清單上方不放過濾膠囊列（主人偏好）。
 * - 每列動作：〔存取紀錄〕在該列下方展開（最近 90 天＝後端保留期，「載入更多」續頁）、
 *   〔編輯〕只有**自己的**且未撤銷才出現（後端 403 `not_owner`／409 `key_revoked`）、
 *   〔撤銷〕未撤銷才出現（任何超級管理者都能撤銷任何一把，不可復原）。
 *   列上不放「⋯」選單鈕（主人偏好）。
 * - 建立兩步：① 名稱／權限範圍／到期 → ② **明文只顯示這一次**。
 *   表單有輸入、或停在第二步時，點背景不關閉（`UiModal` 的 `closeOnBackdrop`）。
 *
 * 勾選與草稿邏輯全在 `lib/apiKeyScopes.ts`、`lib/apiKeys.ts`（純函式，有 spec）。
 */
import { computed, nextTick, onMounted, reactive, ref } from 'vue'

import ApiKeyForm from '@/components/ApiKeyForm.vue'
import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type {
  ApiKeyAccessLogCursor,
  ApiKeyAccessLogRow,
  ApiKeyCreated,
  ApiKeyRow,
  ApiKeyScopeCatalog,
  ApiKeyStatus,
} from '@/api/ls'
import { scopeChips, splitChips } from '@/lib/apiKeyScopes'
import type { ApiKeyDraft } from '@/lib/apiKeys'
import {
  API_KEY_STATUS_LABELS,
  createBody,
  draftFromKey,
  draftProblem,
  emptyDraft,
  fmtLogTime,
  isCreateDirty,
  revokeReasonLabel,
  statusTone,
  updateBody,
} from '@/lib/apiKeys'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { fmtSeen } from '@/lib/staleness'
import { isHoverPointer } from '@/lib/viewport'
import { useAuthStore } from '@/stores/auth'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const dialogs = useDialogsStore()

/** 狀態過濾（下拉）。空字串＝全部。 */
type StatusFilter = ApiKeyStatus | ''
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: '有效' },
  { value: 'expired', label: '已過期' },
  { value: 'revoked', label: '已撤銷' },
  { value: '', label: '全部' },
]
/** 清單上的 scope 膠囊最多顯示幾顆（其餘收成「＋N」）。 */
const CHIP_MAX = 3
/** 一頁存取紀錄的筆數。 */
const LOG_PAGE = 50
/** 存取紀錄的查詢窗＝後端保留期（`api_key_access_log_retention_days`）。後端不帶 since 只給 24 小時。 */
const LOG_WINDOW_DAYS = 90
/** 表格欄數（展開列的 colspan）。 */
const COLS = 7

// 批186：狀態過濾記住。下拉是 `@change` 驅動，程式改值不會觸發，所以清除後要自己重載。
const pf = usePersistedFilters(
  'api-keys',
  { statusFilter: 'active' as StatusFilter },
  { oneOf: { statusFilter: STATUS_OPTIONS.map((o) => o.value) } },
)
const { statusFilter } = pf.refs
const { dirty: filtersDirty } = pf
const clearFilters = () => {
  pf.clear()
  void load()
}
const rows = ref<ApiKeyRow[]>([])
const loading = ref(true)
const loadError = ref('')
const catalog = ref<ApiKeyScopeCatalog | null>(null)
const catalogError = ref('')

function detailOf(e: unknown): unknown {
  return e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
}
function errText(e: unknown, fallback: string): string {
  const d = detailOf(e)
  if (typeof d === 'string') return d
  if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
    return (d as { message: string }).message
  }
  return fallback
}

async function load() {
  loading.value = true
  try {
    rows.value = await ls.listApiKeys({ status: statusFilter.value || undefined })
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取 API 金鑰失敗')
  } finally {
    loading.value = false
  }
}
async function loadCatalog() {
  try {
    catalog.value = await ls.apiKeyScopeCatalog()
    catalogError.value = ''
  } catch (e) {
    catalogError.value = errText(e, '讀取權限範圍目錄失敗')
  }
}
onMounted(async () => {
  await Promise.all([load(), loadCatalog()])
})

/** 這把是不是我的（只有自己的能編輯；比對 email，大小寫不敏感）。 */
function isMine(k: ApiKeyRow): boolean {
  return !!auth.email && !!k.owner_email && k.owner_email.toLowerCase() === auth.email.toLowerCase()
}

function chipsOf(k: ApiKeyRow) {
  const all = catalog.value
    ? scopeChips(k.scopes, catalog.value)
    : k.scopes.map((s) => ({ key: s, text: s, title: s, write: s.endsWith(':write') }))
  return splitChips(all, CHIP_MAX)
}
function hiddenTitle(hidden: { title: string }[]): string {
  return hidden.map((c) => c.title).join('\n')
}

const emptyText = computed(() => {
  switch (statusFilter.value) {
    case 'active':
      return { title: '目前沒有有效的金鑰', sub: '按右上角〔建立金鑰〕建立第一把。' }
    case 'expired':
      return { title: '沒有已過期的金鑰', sub: '' }
    case 'revoked':
      return { title: '沒有已撤銷的金鑰', sub: '' }
    default:
      return { title: '還沒有任何 API 金鑰', sub: '按右上角〔建立金鑰〕建立第一把。' }
  }
})

// ---------------------------------------------------------------------------
// 存取紀錄（展開在該列下方）
// ---------------------------------------------------------------------------
interface LogState {
  rows: ApiKeyAccessLogRow[]
  next: ApiKeyAccessLogCursor | null
  loading: boolean
  loaded: boolean
  error: string
}
const expanded = ref(new Set<string>())
const logs = reactive<Record<string, LogState>>({})

async function loadLogs(keyId: string, more: boolean) {
  // ★ 不能寫 `const st = (logs[keyId] ??= {...})`：那個運算式的值是**原始物件**、不是
  //   reactive 代理，之後改 `st.loading` 畫面不會動。先放進去、再從代理取回來。
  if (!logs[keyId]) logs[keyId] = { rows: [], next: null, loading: false, loaded: false, error: '' }
  const st = logs[keyId]
  if (st.loading) return
  st.loading = true
  try {
    const page = await ls.listApiKeyAccessLogs({
      key_id: keyId,
      since: new Date(Date.now() - LOG_WINDOW_DAYS * 86_400_000).toISOString(),
      cursor: more ? st.next : null,
      limit: LOG_PAGE,
    })
    st.rows = more ? [...st.rows, ...page.rows] : page.rows
    st.next = page.next
    st.loaded = true
    st.error = ''
  } catch (e) {
    st.error = errText(e, '讀取存取紀錄失敗')
  } finally {
    st.loading = false
  }
}
function toggleLogs(k: ApiKeyRow) {
  const next = new Set(expanded.value)
  if (next.has(k.id)) {
    next.delete(k.id)
  } else {
    next.add(k.id)
    // 每次展開都重抓第一頁（紀錄一直在長，留著舊的會讓人以為沒在用）。
    void loadLogs(k.id, false)
  }
  expanded.value = next
}

// ---------------------------------------------------------------------------
// 建立（兩步）
// ---------------------------------------------------------------------------
const createOpen = ref(false)
const createStep = ref<1 | 2>(1)
const createDraft = ref<ApiKeyDraft>(emptyDraft())
const createBusy = ref(false)
const createError = ref('')
/** 第二步的回應（含明文）。關窗即清掉，不留在記憶體裡。 */
const created = ref<ApiKeyCreated | null>(null)
const copied = ref(false)
const copyNote = ref('')
const keyInput = ref<HTMLInputElement | null>(null)

const createProblem = computed(() => draftProblem(createDraft.value, catalog.value))
const createDirty = computed(() => isCreateDirty(createDraft.value))
/** 第一步有輸入就不給點背景關；第二步一律不給（明文只出現這一次）。 */
const createBackdropClose = computed(() => (createStep.value === 1 ? !createDirty.value : false))

function openCreate() {
  createDraft.value = emptyDraft()
  createStep.value = 1
  createError.value = ''
  created.value = null
  copied.value = false
  copyNote.value = ''
  createOpen.value = true
  if (!catalog.value) void loadCatalog()
}

async function submitCreate() {
  const cat = catalog.value
  if (!cat || createProblem.value) return
  createBusy.value = true
  createError.value = ''
  try {
    created.value = await ls.createApiKey(createBody(createDraft.value, cat))
    createStep.value = 2
    copied.value = false
    copyNote.value = ''
    void load()
    await nextTick()
    keyInput.value?.focus()
  } catch (e) {
    createError.value = errText(e, '建立金鑰失敗')
  } finally {
    createBusy.value = false
  }
}

function selectKeyText(e: FocusEvent | MouseEvent) {
  ;(e.target as HTMLInputElement).select()
}

async function copyKey() {
  const k = created.value?.key
  if (!k) return
  let ok = false
  try {
    await navigator.clipboard.writeText(k)
    ok = true
  } catch {
    // 非安全來源或權限被拒：退回選取＋execCommand（舊 API，但所有瀏覽器都還在）。
    const el = keyInput.value
    if (el) {
      el.focus()
      el.select()
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
    }
  }
  if (ok) {
    copied.value = true
    copyNote.value = '已複製到剪貼簿。'
  } else {
    // 批171：觸控裝置沒有 Ctrl+C——改說「長按欄位」。
    copyNote.value = isHoverPointer.value
      ? '無法自動複製——請點選上面的欄位後按 Ctrl+C 手動複製。'
      : '無法自動複製——請長按上面的欄位，選「全選」再「拷貝」。'
  }
}
/** 使用者自己在欄位上按 Ctrl+C 也算複製過。 */
function onManualCopy() {
  copied.value = true
  copyNote.value = '已複製到剪貼簿。'
}

function resetCreate() {
  createOpen.value = false
  created.value = null
  createDraft.value = emptyDraft()
  createStep.value = 1
}

async function closeCreate() {
  // 確認框開著時又按 Esc（UiModal 掛在 window 上聽）：交給那個確認框，不再疊一個。
  if (dialogs.current) return
  if (createStep.value === 2 && !copied.value) {
    const ok = await dialogs.confirm({
      title: '還沒有複製金鑰',
      message: '關閉之後就再也看不到這把金鑰的明文了（只能撤銷後重建）。確定已經保存好了嗎？',
      confirmText: '已保存，關閉',
      cancelText: '回去複製',
    })
    if (!ok) return
  }
  resetCreate()
}

// ---------------------------------------------------------------------------
// 編輯
// ---------------------------------------------------------------------------
const editing = ref<ApiKeyRow | null>(null)
const editDraft = ref<ApiKeyDraft>(emptyDraft())
const editBusy = ref(false)
const editError = ref('')

const editProblem = computed(() =>
  editing.value ? draftProblem(editDraft.value, catalog.value, { base: editing.value }) : null,
)
const editChanges = computed(() =>
  editing.value && catalog.value ? updateBody(editDraft.value, editing.value, catalog.value) : {},
)
const editDirty = computed(() => Object.keys(editChanges.value).length > 0)

function openEdit(k: ApiKeyRow) {
  editing.value = k
  editDraft.value = draftFromKey(k)
  editError.value = ''
  if (!catalog.value) void loadCatalog()
}
function closeEdit() {
  if (dialogs.current) return
  editing.value = null
}

async function saveEdit() {
  const k = editing.value
  if (!k || editProblem.value) return
  if (!editDirty.value) {
    editing.value = null
    return
  }
  editBusy.value = true
  editError.value = ''
  try {
    await ls.updateApiKey(k.id, editChanges.value)
    editing.value = null
    await load()
  } catch (e) {
    editError.value = errText(e, '儲存金鑰失敗')
  } finally {
    editBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// 撤銷
// ---------------------------------------------------------------------------
const revoking = ref<string | null>(null)

async function revoke(k: ApiKeyRow) {
  const mine = isMine(k)
  const ok = await dialogs.confirm({
    title: `撤銷「${k.name}」`,
    message:
      `撤銷之後，這把金鑰（${k.prefix}…）的下一個請求就會被拒絕，而且不能復原——要再用只能重建一把新的。` +
      (mine ? '' : `\n\n這把金鑰屬於 ${k.owner_email ?? '另一位超級管理者'}，撤銷前建議先知會對方。`) +
      '\n\n確定要撤銷嗎？',
    danger: true,
    confirmText: '撤銷',
  })
  if (!ok) return
  revoking.value = k.id
  try {
    await ls.revokeApiKey(k.id)
    await load()
  } catch (e) {
    await dialogs.notify({ title: '撤銷失敗', message: errText(e, '撤銷金鑰失敗') })
  } finally {
    revoking.value = null
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>API 金鑰</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <label class="hf">
        <span>狀態</span>
        <select v-model="statusFilter" @change="load()">
          <option v-for="o in STATUS_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
      </label>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
      <UiButton variant="primary" @click="openCreate">＋ 建立金鑰</UiButton>
    </div>

    <p class="subtle intro">
      金鑰讓程式（腳本、Claude Code、MCP）以<strong>你的身分</strong>呼叫後台同一組 API；
      能做的事＝擁有者（超級管理者）的權限 ∩ 金鑰勾選的範圍。只能建立與修改自己的金鑰，
      任何超級管理者都能撤銷任何一把；擁有者帳號停用、降級、被強制登出或刪除時會自動撤銷。
    </p>

    <div v-if="loading && !rows.length" class="loading-wrap"><span class="spinner lg" /></div>

    <div v-else class="card">
      <p v-if="loadError" class="err">{{ loadError }}</p>

      <div v-if="!rows.length && !loadError" class="empty">
        <div class="empty-title">{{ emptyText.title }}</div>
        <div v-if="emptyText.sub" class="empty-sub">{{ emptyText.sub }}</div>
      </div>

      <div v-else-if="rows.length" class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table keys">
          <thead>
            <tr>
              <th>名稱・前綴</th>
              <th>擁有者</th>
              <th>權限範圍</th>
              <th>狀態</th>
              <th>最後使用</th>
              <th>到期・建立</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="k in rows" :key="k.id">
              <tr :class="{ gone: k.status !== 'active', open: expanded.has(k.id) }">
                <td class="name">
                  <div class="kname">{{ k.name }}</div>
                  <code class="mono">{{ k.prefix }}…</code>
                </td>
                <td class="owner">
                  <!-- email 與「你」包成一個不折行的單位：分開放的話徽章會被擠到第二行，
                       看起來像多一列空白（截圖驗收時抓到的）。 -->
                  <span class="owner-line">{{ k.owner_email ?? '—' }}<span v-if="isMine(k)" class="me-badge">你</span></span>
                </td>
                <td class="scopes">
                  <div class="chips">
                    <span
                      v-for="c in chipsOf(k).shown"
                      :key="c.key"
                      class="chip"
                      :class="{ w: c.write }"
                      :title="c.title"
                    >
                      {{ c.text }}
                    </span>
                    <span
                      v-if="chipsOf(k).hidden.length"
                      class="chip more"
                      :title="hiddenTitle(chipsOf(k).hidden)"
                    >
                      ＋{{ chipsOf(k).hidden.length }}
                    </span>
                  </div>
                </td>
                <td class="nowrap">
                  <span class="status-pill" :class="k.status">{{ API_KEY_STATUS_LABELS[k.status] ?? k.status }}</span>
                  <div
                    v-if="k.status === 'revoked'"
                    class="sub"
                    :title="`撤銷時間 ${fmtSeen(k.revoked_at)}${k.revoked_by ? `，由 ${k.revoked_by}` : ''}`"
                  >
                    {{ revokeReasonLabel(k.revoked_reason) }}
                  </div>
                </td>
                <td class="when">
                  <template v-if="k.last_used_at">
                    <div>{{ fmtSeen(k.last_used_at, { short: true }) }}</div>
                    <div class="sub">{{ k.last_used_ip ?? '—' }}</div>
                  </template>
                  <span v-else class="subtle">從未使用</span>
                </td>
                <!-- 到期・建立合成一欄兩行（同帳號管理的「最後登入・建立」）：分開兩欄在 1366 會橫捲。 -->
                <td class="when">
                  <div>
                    <span v-if="!k.expires_at" class="subtle">無期限</span>
                    <template v-else>{{ fmtSeen(k.expires_at, { short: true }) }}</template>
                  </div>
                  <div class="sub">建立 {{ fmtSeen(k.created_at, { short: true }) }}</div>
                </td>
                <td class="acts-cell">
                  <div class="acts">
                    <!-- 〔存取紀錄〕永遠在最左、字不隨展開狀態改變（只換外框）：
                         後兩顆有條件出現，放在它後面才不會讓它跟著跳位。 -->
                    <UiButton
                      variant="secondary"
                      size="sm"
                      :class="{ pressed: expanded.has(k.id) }"
                      :aria-expanded="expanded.has(k.id)"
                      @click="toggleLogs(k)"
                    >
                      存取紀錄
                    </UiButton>
                    <UiButton
                      v-if="isMine(k) && k.status !== 'revoked'"
                      variant="secondary"
                      size="sm"
                      @click="openEdit(k)"
                    >
                      編輯
                    </UiButton>
                    <UiButton
                      v-if="k.status !== 'revoked'"
                      variant="danger"
                      size="sm"
                      :loading="revoking === k.id"
                      @click="revoke(k)"
                    >
                      撤銷
                    </UiButton>
                  </div>
                </td>
              </tr>

              <!-- 存取紀錄（該列下方展開） -->
              <tr v-if="expanded.has(k.id)" class="logrow">
                <td :colspan="COLS">
                  <div class="logbox">
                    <div class="log-head">
                      <span class="log-title">存取紀錄</span>
                      <span class="subtle">最近 {{ LOG_WINDOW_DAYS }} 天（含被拒絕的請求），新到舊</span>
                    </div>
                    <p v-if="logs[k.id]?.error" class="err">{{ logs[k.id].error }}</p>
                    <div v-if="!logs[k.id]?.loaded && logs[k.id]?.loading" class="log-loading">
                      <span class="spinner" />
                    </div>
                    <p v-else-if="logs[k.id]?.loaded && !logs[k.id].rows.length" class="subtle log-empty">
                      最近 {{ LOG_WINDOW_DAYS }} 天沒有這把金鑰的存取紀錄。
                    </p>
                    <table v-else-if="logs[k.id]?.rows.length" v-card-table class="tbl card-table inner">
                      <thead>
                        <tr><th>時間</th><th>方法</th><th>路徑</th><th>狀態碼</th><th>IP</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="r in logs[k.id].rows" :key="r.id">
                          <td class="when">{{ fmtLogTime(r.created_at) }}</td>
                          <td class="nowrap"><code class="mono">{{ r.method }}</code></td>
                          <td class="path">{{ r.path }}</td>
                          <td class="nowrap">
                            <span class="code" :class="statusTone(r.status)">{{ r.status }}</span>
                          </td>
                          <td class="nowrap">{{ r.ip ?? '—' }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="logs[k.id]?.next" class="more-row">
                      <UiButton size="sm" variant="secondary" :loading="logs[k.id].loading" @click="loadLogs(k.id, true)">
                        載入更多
                      </UiButton>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 建立（兩步） -->
    <UiModal
      :open="createOpen"
      :title="createStep === 1 ? '建立 API 金鑰' : '金鑰已建立'"
      width="720px"
      :close-on-backdrop="createBackdropClose"
      @close="closeCreate"
    >
      <template v-if="createStep === 1">
        <p v-if="catalogError" class="err">{{ catalogError }}</p>
        <div v-if="!catalog && !catalogError" class="loading-wrap"><span class="spinner" /></div>
        <ApiKeyForm v-if="catalog" v-model="createDraft" :catalog="catalog" />
        <p v-if="createError" class="err">{{ createError }}</p>
      </template>

      <template v-else-if="created">
        <div class="once-warn" role="alert">
          <strong>這把金鑰只會顯示這一次，請立即複製保存。</strong>
          關閉這個視窗之後就再也看不到明文了；遺失只能撤銷後重建。
        </div>
        <label class="field">
          <span class="field-label">金鑰（{{ created.name }}）</span>
          <div class="key-row">
            <input
              ref="keyInput"
              class="mono key-in"
              type="text"
              :value="created.key"
              readonly
              spellcheck="false"
              @focus="selectKeyText"
              @click="selectKeyText"
              @copy="onManualCopy"
            />
            <UiButton variant="primary" type="button" @click="copyKey">複製</UiButton>
          </div>
          <!-- 固定佔一行：「已複製」出現時下方的內容不會被往下推。 -->
          <small class="copy-note" :class="{ ok: copied }">{{ copyNote || ' ' }}</small>
        </label>
        <p class="subtle usage">
          用法：HTTP 標頭帶 <code class="mono">Authorization: Bearer &lt;金鑰&gt;</code>，
          呼叫與後台網頁同一組 <code class="mono">/api/admin/*</code>。
        </p>
      </template>

      <template #foot>
        <template v-if="createStep === 1">
          <!-- 剛打開還沒輸入時不唸「請填寫名稱」；開始填之後才告訴他還缺什麼。 -->
          <span v-if="catalog && createProblem && createDirty" class="foot-hint">{{ createProblem }}</span>
          <UiButton @click="closeCreate">取消</UiButton>
          <UiButton
            variant="primary"
            :loading="createBusy"
            :disabled="!catalog || !!createProblem"
            @click="submitCreate"
          >
            建立金鑰
          </UiButton>
        </template>
        <UiButton v-else variant="primary" @click="closeCreate">完成</UiButton>
      </template>
    </UiModal>

    <!-- 編輯（明文不再出現） -->
    <UiModal
      :open="!!editing"
      :title="editing ? `編輯金鑰 — ${editing.name}` : ''"
      width="720px"
      :close-on-backdrop="!editDirty"
      @close="closeEdit"
    >
      <template v-if="editing">
        <p class="subtle edit-meta">
          前綴 <code class="mono">{{ editing.prefix }}…</code>・建立於 {{ fmtSeen(editing.created_at) }}・
          {{ API_KEY_STATUS_LABELS[editing.status] ?? editing.status }}
          <br />明文不會再顯示；改權限範圍或到期下一個請求就生效。
        </p>
        <p v-if="catalogError" class="err">{{ catalogError }}</p>
        <ApiKeyForm v-if="catalog" v-model="editDraft" :catalog="catalog" />
        <p v-if="editError" class="err">{{ editError }}</p>
      </template>
      <template #foot>
        <span v-if="editProblem" class="foot-hint">{{ editProblem }}</span>
        <UiButton @click="closeEdit">取消</UiButton>
        <UiButton
          variant="primary"
          :loading="editBusy"
          :disabled="!catalog || !!editProblem || !editDirty"
          @click="saveEdit"
        >
          儲存
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.intro {
  margin: 0 0 var(--sp-3);
  line-height: var(--lh-base);
}
.err {
  margin: var(--sp-2) 0;
  color: var(--danger);
  font-size: var(--fs-12);
}
.nowrap {
  white-space: nowrap;
}
/* 合欄的第二行（與帳號管理同一套寫法）。 */
.sub {
  margin-top: 2px;
  font-size: var(--fs-11);
  color: var(--fg-3);
}
.name {
  min-width: 150px;
  max-width: 220px;
}
.kname {
  font-weight: 600;
  color: var(--fg);
  overflow-wrap: anywhere;
  margin-bottom: 2px;
}
/* email＋「你」一行排完（我方信箱約 175px＋徽章 24px）。擁有者必為超級管理者（我方員工），
   不會出現代理商那種超長信箱，所以直接不折行。 */
.owner {
  white-space: nowrap;
}
.me-badge {
  display: inline-block;
  margin-left: 4px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--bg);
  color: var(--fg-3);
  font-size: var(--fs-11);
  vertical-align: middle;
}
.scopes {
  min-width: 200px;
  max-width: 300px;
}
.scopes .chips {
  gap: 4px;
}
/* 含寫入的類別用琥珀色（與勾選格的「寫入」同色）。 */
.chip.w {
  background: var(--st-pending-bg);
  border-color: transparent;
  color: var(--st-pending-fg);
}
.chip.more {
  cursor: help;
  color: var(--fg-3);
}
.when {
  white-space: nowrap;
}
tr.gone td {
  color: var(--fg-3);
}
tr.gone .kname {
  color: var(--fg-2);
}
/* 展開中的列與下方紀錄視為一組：主列不畫底線，紀錄列用淡底襯出來。 */
tr.open td {
  border-bottom-color: transparent;
}
.tbl tbody tr.logrow:hover {
  background: transparent;
}
.logrow > td {
  padding: 0 12px 12px;
}
.logbox {
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
  padding: var(--sp-3);
}
.log-head {
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
  flex-wrap: wrap;
  margin-bottom: var(--sp-2);
}
.log-title {
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--fg);
}
.log-loading {
  display: flex;
  justify-content: center;
  padding: var(--sp-3);
}
.log-empty {
  margin: 0;
}
.tbl.inner {
  background: var(--bg-panel);
  border: 1px solid var(--border-2);
  border-radius: var(--r-sm);
}
.tbl.inner thead th {
  padding: 6px 10px;
  background: var(--bg-panel);
}
.tbl.inner tbody td {
  padding: 6px 10px;
}
.path {
  min-width: 220px;
  font-family: var(--font-mono);
  font-size: var(--fs-11);
  overflow-wrap: anywhere;
}
.code {
  display: inline-block;
  min-width: 38px;
  padding: 0 6px;
  border-radius: var(--r-xs);
  font-family: var(--font-mono);
  font-size: var(--fs-11);
  font-weight: 600;
  text-align: center;
}
.code.ok {
  background: var(--st-active-bg);
  color: var(--st-active-fg);
}
.code.warn {
  background: var(--st-pending-bg);
  color: var(--st-pending-fg);
}
.code.bad {
  background: rgba(209, 67, 67, 0.15);
  color: var(--danger);
}
.more-row {
  display: flex;
  justify-content: center;
  margin-top: var(--sp-2);
}
/* 〔存取紀錄〕展開中：只換框色與底色，字與尺寸不變（切換不跳動）。 */
.btn.pressed {
  border-color: var(--brand-500);
  background: var(--brand-50);
  color: var(--brand-700);
}
[data-theme='dark'] .btn.pressed {
  background: var(--bg-active);
  color: var(--brand-400);
}

/* --- 建立第二步：明文 --- */
.once-warn {
  margin-bottom: var(--sp-4);
  padding: var(--sp-3);
  border: 1px solid var(--warning);
  border-radius: var(--r-md);
  background: var(--st-pending-bg);
  color: var(--st-pending-fg);
  font-size: var(--fs-13);
  line-height: var(--lh-base);
}
.once-warn strong {
  display: block;
  margin-bottom: 2px;
}
.key-row {
  display: flex;
  gap: var(--sp-2);
  align-items: center;
}
/* 等寬字：明文裡 l／I／1、O／0 要分得出來（有人會照著抄）。 */
.key-in {
  flex: 1;
  min-width: 0;
  font-family: var(--font-mono);
  font-size: var(--fs-12);
}
.copy-note {
  font-size: var(--fs-12);
  color: var(--fg-3);
  min-height: 1.4em;
}
.copy-note.ok {
  color: var(--success);
}
.usage {
  margin: var(--sp-3) 0 0;
  line-height: var(--lh-base);
}
.edit-meta {
  margin: 0 0 var(--sp-3);
  line-height: var(--lh-base);
}
/* 對話框底列左側的提示（為什麼「建立／儲存」按不下去）。 */
.foot-hint {
  margin-right: auto;
  align-self: center;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
</style>
